import { type Query, useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import {
  type ChangeEvent,
  Fragment,
  useCallback,
  useContext,
  useMemo,
  useEffect,
  useState,
  FormEvent
} from 'react';
import { type ChangeHandler, Controller, useFieldArray, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import {
  controlLineVisibleAttestationOptions,
  drugScreenTestTypeOrder,
  memberInAttendanceOptions,
  temperatureInRangeAttestationOptions,
  testStripResultOptions
} from '@/constants/drugScreen';
import emptySelectOption from '@/constants/emptySelectOption';
import AppContext from '@/contexts/appContext';
import DrugScreenPayoutPreview from '@/forms/DrugScreenForm/components/DrugScreenPayoutPreview';
import FieldError from '@/forms/FieldError';
import useAPI from '@/hooks/useAPI';
import PersonContext from '@/pages/Person/PersonContext';
import DrugScreenRepository from '@/repositories/DrugScreenRepository';
import type { DrugScreen } from '@/types/drugScreen';
import Alert from '@/ui/components/Alert';
import AutocompleteSingleSelect from '@/ui/components/AutocompleteSingleSelect';
import Button from '@/ui/components/Button';
import Card from '@/ui/components/Card';
import InputField from '@/ui/components/Form/InputField';
import InputGroup from '@/ui/components/Form/InputGroup';
import Label from '@/ui/components/Form/Label';
import RadioGroup from '@/ui/components/Form/RadioGroup';
import RichTextEditor from '@/ui/components/Form/RichTextEditor';
import type { OptionType } from '@/ui/components/Form/Select/types';
import Spinner from '@/ui/components/Spinner';
import mapAxiosErrorMessages from '@/utils/mapAxiosErrorMessages';
import snakeCaseToTitleCase from '@/utils/snakeToTitleCase';

import type { DrugScreenFormValues, DrugScreenPayload, Props } from './types';

const invalidateQueriesPredicate = ({ queryKey: [baseKey] }: Query) =>
  typeof baseKey === 'string'
    ? [
        'getEncountersByHealthieId',
        'getEncounterById',
        'getAppointments',
        'getPersonDrugScreen',
        'getPersonDrugScreens',
        'getPersonDrugScreensCounts'
      ].includes(baseKey)
    : false;

const controlLineVisibleAttestationOptionsForSelect: OptionType<string>[] = Object.entries(
  controlLineVisibleAttestationOptions
).map(([value, label]) => ({ label, value }));

const temperatureInRangeAttestationOptionsForSelect: OptionType<string>[] = Object.entries(
  temperatureInRangeAttestationOptions
).map(([value, label]) => ({ label, value }));

const testStripResultOptionsForSelect: OptionType<string>[] = testStripResultOptions.map(
  (option) => ({
    label: option,
    value: option.toLowerCase()
  })
);

const DrugScreenForm = ({ appointmentId, drugScreen }: Props) => {
  const [showPreviewModal, setShowPreviewModal] = useState<{
    showModal: boolean;
    values: DrugScreenPayload | null;
  }>({
    showModal: false,
    values: null
  });

  // [NOTE]: When submitting the form we run a preview for the payout, which pauses the
  // actual create/update submission. When this preview is confirmed, we resolve the
  // promise to continue with the actual submission.
  const [resolveDrugScreenPreviewPromise, setResolveDrugScreenPreviewPromise] = useState<
    ((value: boolean) => void) | null
  >(null);
  const navigate = useNavigate();
  const { addMessage, currentUser } = useContext(AppContext);
  const {
    person: { id: personId, jurisdiction }
  } = useContext(PersonContext);
  const queryClient = useQueryClient();
  const drugScreenRepository = DrugScreenRepository();

  const isMutableForm = !!appointmentId && (!drugScreen || drugScreen.is_mutable);
  const {
    data: defaultDrugScreenData,
    isPending: isLoadingDefaultDrugScreenData,
    isSuccess
  } = useAPI({
    queryKey: ['defaultDrugScreenData', personId],
    queryFn: () => drugScreenRepository.getDefaultDrugScreenData(personId),
    enabled: !!appointmentId || !!drugScreen
  });

  const { isPending: isPendingCreation, mutate: createDrugScreen } = useMutation({
    mutationFn: (values: DrugScreenPayload) => drugScreenRepository.createDrugScreen(values),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        predicate: invalidateQueriesPredicate
      });
      addMessage({ level: 'success', text: 'Successfully saved drug screen' });
      navigate(-1);
    },
    onError: (error: AxiosError) => {
      addMessage(mapAxiosErrorMessages(error));
    }
  });

  const { isPending: isPendingUpdate, mutate: updateDrugScreen } = useMutation({
    mutationFn: (values: DrugScreenPayload) =>
      drugScreenRepository.updateDrugScreen(drugScreen?.id || '', values),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        predicate: invalidateQueriesPredicate
      });
      addMessage({ level: 'success', text: 'Successfully updated drug screen' });
      navigate(-1);
    },
    onError: (error: AxiosError) => {
      addMessage(mapAxiosErrorMessages(error));
    }
  });

  const [initialValues, setInitialValues] = useState<DrugScreenFormValues>({
    member_in_attendance: { label: 'Yes', value: 'yes' },
    drug_screen_test_type: emptySelectOption,
    test_strips: [],
    temperature_in_range_attestation: null,
    control_line_visible_attestation: null,
    test_expiration_on: null,
    lot_number: '',
    drug_screen_result_reason: [],
    notes: ''
  });

  useEffect(() => {
    if (isSuccess && drugScreen && defaultDrugScreenData.data)
      setInitialValues({
        member_in_attendance: {
          label: snakeCaseToTitleCase(drugScreen.member_in_attendance),
          value: drugScreen.member_in_attendance
        },
        drug_screen_test_type: drugScreen.drug_screen_test_type
          ? {
              label:
                defaultDrugScreenData.data.drug_screen_test_types.find(
                  (drugScreenTestType) =>
                    drugScreenTestType.id === drugScreen.drug_screen_test_type?.id
                )?.name ?? '',
              value: drugScreen.drug_screen_test_type.id
            }
          : emptySelectOption,
        test_strips: drugScreen.drug_screen_test_type
          ? (defaultDrugScreenData.data.drug_screen_test_types
              .find(
                (drugScreenTestType) =>
                  drugScreenTestType.id === drugScreen.drug_screen_test_type?.id
              )
              ?.test_strips.map(({ internal_drug_field, name }) => ({
                substanceInternalField: internal_drug_field,
                substanceName: name,
                result:
                  testStripResultOptionsForSelect.find(
                    ({ value }) => value === drugScreen[name as keyof DrugScreen]
                  ) ?? emptySelectOption
              })) ?? [])
          : [],
        temperature_in_range_attestation: drugScreen.temperature_in_range_attestation
          ? {
              label:
                temperatureInRangeAttestationOptions[drugScreen.temperature_in_range_attestation],
              value: drugScreen.temperature_in_range_attestation
            }
          : null,
        control_line_visible_attestation: drugScreen.control_line_visible_attestation
          ? {
              label:
                controlLineVisibleAttestationOptions[drugScreen.control_line_visible_attestation],
              value: drugScreen.control_line_visible_attestation
            }
          : null,
        test_expiration_on: drugScreen.test_expiration_on,
        lot_number: drugScreen.lot_number || '',
        drug_screen_result_reason: drugScreen.drug_screen_result_reason.map(({ id }) => id),
        notes: drugScreen.notes ?? ''
      });
  }, [defaultDrugScreenData, drugScreen, isSuccess]);

  const {
    clearErrors,
    control,
    formState: { errors, isSubmitting },
    getValues,
    handleSubmit,
    register,
    setValue,
    trigger,
    watch
  } = useForm({ values: initialValues });
  const values = watch();
  const isDrugScreenPerformed = values.member_in_attendance.value === 'yes';

  const { append, fields, remove } = useFieldArray({
    control,
    name: 'test_strips',
    shouldUnregister: true
  });

  const drugScreenTestTypesForSelect = useMemo(
    () =>
      defaultDrugScreenData?.data?.drug_screen_test_types
        ?.filter(({ active }) => active)
        .sort((a, b) =>
          drugScreenTestTypeOrder[a.kind] < drugScreenTestTypeOrder[b.kind]
            ? -1
            : drugScreenTestTypeOrder[a.kind] > drugScreenTestTypeOrder[b.kind]
              ? 1
              : a.name.toLowerCase() < b.name.toLowerCase()
                ? -1
                : a.name.toLowerCase() > b.name.toLowerCase()
                  ? 1
                  : 0
        )
        .map(({ id, name }) => ({
          label: name,
          value: id
        })) ?? [],
    [defaultDrugScreenData?.data?.drug_screen_test_types]
  );

  const testStripsToArrayField = useCallback(
    (drugScreenTestTypeId: string | undefined) => {
      const testStrips = defaultDrugScreenData?.data?.drug_screen_test_types?.find(
        (drugScreenTestType) => drugScreenTestType.id === drugScreenTestTypeId
      )?.test_strips;

      if (!testStrips?.length) return;

      append(
        [...testStrips]
          .sort((a, b) => a.name.localeCompare(b.name))
          .map(({ internal_drug_field, name }) => ({
            substanceInternalField: internal_drug_field,
            substanceName: name,
            result:
              testStripResultOptionsForSelect.find(({ value }) => value === 'negative') ??
              emptySelectOption
          }))
      );
    },
    [append, defaultDrugScreenData?.data?.drug_screen_test_types]
  );

  const handleDrugScreenTestTypeChange = (
    selectedOption: OptionType<string> | null,
    onChange: (value: OptionType | null) => void
  ) => {
    onChange(selectedOption);
    remove();
    testStripsToArrayField(selectedOption?.value);
  };

  const handleDrugScreenResultReasonChange = (
    event: ChangeEvent<HTMLInputElement>,
    reasonName: string,
    onChange: ChangeHandler
  ) => {
    onChange(event);

    if (
      event.target.checked &&
      reasonName.toLowerCase().includes('file a self-report drug screen')
    ) {
      const selfReportDrugScreenTestType = drugScreenTestTypesForSelect.find(
        ({ label }) => label === 'Care Team - Self Report - 3 panel test'
      );
      if (
        selfReportDrugScreenTestType &&
        values.drug_screen_test_type.value !== selfReportDrugScreenTestType?.value
      ) {
        setValue('drug_screen_test_type', selfReportDrugScreenTestType, { shouldValidate: true });
        remove();
        testStripsToArrayField(selfReportDrugScreenTestType.value);
      }
    }
  };

  const drugScreenPayload = (values: DrugScreenFormValues): DrugScreenPayload => {
    return {
      appointment_id: appointmentId as string,
      conducted_by: currentUser ? { email: currentUser.email } : undefined,
      drug_screen_test_type_id: values.drug_screen_test_type.value || null,
      member_in_attendance: values.member_in_attendance.value,
      person_id: personId,
      control_line_visible_attestation: values.control_line_visible_attestation?.value || null,
      temperature_in_range_attestation: values.temperature_in_range_attestation?.value || null,
      test_expiration_on: values.test_expiration_on || null,
      lot_number: values.lot_number || null,
      drug_screen_result_reason_ids: values.drug_screen_result_reason,
      notes: values.notes,
      ...values.test_strips.reduce((testStripsObj, { result, substanceName }) => {
        return {
          ...testStripsObj,
          [substanceName]: result.value
        };
      }, {})
    };
  };

  const submitForm = async (values: DrugScreenFormValues) => {
    const payload = drugScreenPayload(values);

    if (drugScreen?.id) {
      updateDrugScreen(payload);
    } else {
      createDrugScreen(payload);
    }
  };

  const performDrugScreenPayoutPreview = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const isValid = await trigger();

    if (isValid) {
      if (appointmentId) {
        const values = getValues();
        const payload: DrugScreenPayload = drugScreenPayload(values);

        if (!payload.drug_screen_test_type_id) delete payload.drug_screen_test_type_id;

        setShowPreviewModal((prevState) => ({ ...prevState, showModal: true, values: payload }));

        const payoutPreviewPromiseConfirmation = await new Promise((resolve) => {
          setResolveDrugScreenPreviewPromise(() => resolve);
        });
        // [NOTE]: If user confirms the payout preview modal, the promise will
        // return true. If it is declined, the promise will return false.
        if (payoutPreviewPromiseConfirmation) {
          await handleSubmit(submitForm)();
        }

        setShowPreviewModal((prevState) => ({ ...prevState, showModal: false }));
      }
    }
  };

  const handlePreviewConfirm = (value: boolean) => {
    if (resolveDrugScreenPreviewPromise) {
      resolveDrugScreenPreviewPromise(value);
      setShowPreviewModal((prevState) => ({ ...prevState, showModal: false }));
    }
  };

  if (!appointmentId && !drugScreen)
    return <Alert level="error">No Appointment ID provided or Drug Screen data received</Alert>;

  return (
    <Card>
      <form onSubmit={performDrugScreenPayoutPreview} className="flex flex-col gap-2">
        <InputGroup>
          <Controller
            name="member_in_attendance"
            control={control}
            render={({
              field: {
                onChange,
                value: { value }
              }
            }) => (
              <RadioGroup
                disabled={!isMutableForm}
                id="drug-screen-member-in-attendance"
                label="Member in attendance"
                options={memberInAttendanceOptions.map(({ className, label, value }) => ({
                  className,
                  label,
                  value
                }))}
                optionsClassName="space-y-1"
                selectedValue={value}
                onChange={(value) => {
                  clearErrors('drug_screen_test_type');
                  onChange({ label: snakeCaseToTitleCase(String(value)), value });
                }}
              />
            )}
            rules={{
              validate: {
                required: (value) => !!value.value || 'Member in attendance is required'
              }
            }}
          />
          <FieldError message={errors.member_in_attendance?.message} />
        </InputGroup>

        {isDrugScreenPerformed && (
          <>
            <InputGroup>
              <Controller
                name="drug_screen_test_type"
                control={control}
                render={({
                  field: {
                    onChange,
                    value: { value }
                  }
                }) =>
                  isLoadingDefaultDrugScreenData ? (
                    <Spinner label="Loading drug screen types..." />
                  ) : (
                    <AutocompleteSingleSelect
                      disabled={!isMutableForm}
                      label="Drug Screen Type"
                      options={drugScreenTestTypesForSelect}
                      placeholder="Select the Drug Screen Type"
                      onChange={(newValue) => handleDrugScreenTestTypeChange(newValue, onChange)}
                      selectedOption={
                        drugScreenTestTypesForSelect.find((option) => option.value === value) ??
                        emptySelectOption
                      }
                    />
                  )
                }
                rules={{
                  validate: {
                    required: (value) =>
                      values.member_in_attendance.value === 'no' ||
                      !!value.value ||
                      'Drug Screen Type is required'
                  }
                }}
              />
              <FieldError message={errors.drug_screen_test_type?.message} />
            </InputGroup>

            {/* Dynamic Test Strips according to Drug Test Type */}
            {fields.map(({ id, substanceInternalField, substanceName }, idx) => (
              <Fragment key={`test-strip-${id}-${idx}`}>
                <InputGroup>
                  <Controller
                    name={`test_strips.${idx}.result`}
                    control={control}
                    render={({
                      field: {
                        onChange,
                        value: { value }
                      }
                    }) => (
                      <AutocompleteSingleSelect
                        disabled={!isMutableForm}
                        label={`${substanceInternalField} - ${snakeCaseToTitleCase(substanceName)}`}
                        placeholder={`Select result for ${substanceInternalField}`}
                        options={testStripResultOptionsForSelect}
                        onChange={onChange}
                        selectedOption={
                          testStripResultOptionsForSelect.find(
                            (option) => option.value === value
                          ) ?? emptySelectOption
                        }
                      />
                    )}
                  />
                  <FieldError message={errors.test_strips?.[idx]?.message} />
                </InputGroup>
              </Fragment>
            ))}

            {/* Additional questions for Urine kind */}
            {values.drug_screen_test_type.label.toLowerCase().includes('urine') && (
              <>
                <InputGroup>
                  <Controller
                    name="temperature_in_range_attestation"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <RadioGroup
                        disabled={!isMutableForm}
                        id="drug-screen-temperature-in-range-attestation"
                        label="Temperature of Urine drug test"
                        options={temperatureInRangeAttestationOptionsForSelect}
                        selectedValue={value?.value ?? null}
                        onChange={(selectedValue) =>
                          onChange(
                            temperatureInRangeAttestationOptionsForSelect.find(
                              ({ value }) => value === selectedValue
                            )
                          )
                        }
                      />
                    )}
                    rules={{
                      validate: {
                        required: (value) =>
                          !!value?.value || 'Temperature of Urine drug test is required'
                      }
                    }}
                  />
                  <FieldError message={errors.temperature_in_range_attestation?.message} />
                </InputGroup>

                <InputGroup>
                  <Controller
                    name="control_line_visible_attestation"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <RadioGroup
                        disabled={!isMutableForm}
                        id="drug-screen-control-line-visible-attestation"
                        label="The control line for every analyte"
                        options={controlLineVisibleAttestationOptionsForSelect}
                        selectedValue={value?.value ?? null}
                        onChange={(selectedValue) =>
                          onChange(
                            controlLineVisibleAttestationOptionsForSelect.find(
                              ({ value }) => value === selectedValue
                            )
                          )
                        }
                      />
                    )}
                    rules={{
                      validate: {
                        required: (value) =>
                          !!value?.value || 'Control line attestation is required'
                      }
                    }}
                  />
                  <FieldError message={errors.control_line_visible_attestation?.message} />
                </InputGroup>

                {/* Additional questions for Urine kind AND Nevada jurisdiction */}
                {jurisdiction?.name?.toLowerCase().includes('nevada') && (
                  <>
                    <InputGroup>
                      <Controller
                        name="test_expiration_on"
                        control={control}
                        render={({ field }) => {
                          return (
                            <InputField
                              {...field}
                              disabled={!isMutableForm}
                              label="Text expiration date"
                              id="drug-screen-control-test-expiration-on"
                              type="date"
                              value={field.value ?? ''}
                            />
                          );
                        }}
                        rules={{ required: 'Test expiration date is required' }}
                      />
                      <FieldError message={errors.test_expiration_on?.message} />
                    </InputGroup>

                    <InputGroup>
                      <Controller
                        name="lot_number"
                        control={control}
                        render={({ field }) => (
                          <InputField
                            {...field}
                            disabled={!isMutableForm}
                            label="Lot number"
                            id="drug-screen-control-lot-number"
                            placeholder="Enter the lot number"
                            type="text"
                          />
                        )}
                        rules={{ required: 'Lot number is required' }}
                      />
                      <FieldError message={errors.lot_number?.message} />
                    </InputGroup>
                  </>
                )}
              </>
            )}

            <InputGroup>
              <fieldset disabled={!isMutableForm} name="drug_screen_result_reason">
                <legend className="block my-1 font-medium text-gray-700">Result Tags</legend>
                {isLoadingDefaultDrugScreenData ? (
                  <Spinner label="Loading result tags..." />
                ) : defaultDrugScreenData?.data?.drug_screen_result_reasons?.length ? (
                  defaultDrugScreenData.data.drug_screen_result_reasons.map(
                    ({ active, id: reasonId, name }) => {
                      const id = `drug-screen-result-tag-${reasonId}`;

                      if (!active) return null;

                      const { onChange, ...registerRest } = register('drug_screen_result_reason');

                      return (
                        <Fragment key={id}>
                          <Label className="leading-relaxed mb-1" htmlFor={id} reverse text={name}>
                            <input
                              {...registerRest}
                              className="align-middle mr-1"
                              id={id}
                              type="checkbox"
                              onChange={(e) =>
                                handleDrugScreenResultReasonChange(e, name, onChange)
                              }
                              value={reasonId}
                            />
                          </Label>
                        </Fragment>
                      );
                    }
                  )
                ) : null}
              </fieldset>
              <FieldError message={errors.drug_screen_result_reason?.message} />
            </InputGroup>
          </>
        )}

        <InputGroup>
          <legend className="block my-1 text-sm font-medium text-gray-700">Notes</legend>
          <Controller
            name="notes"
            control={control}
            render={({ field: { onChange, value } }) => {
              return <RichTextEditor onChange={onChange} value={value} />;
            }}
          />
          <FieldError message={errors.notes?.message} />
        </InputGroup>

        <InputGroup>
          <Button
            className="w-full"
            disabled={isPendingCreation || isPendingUpdate || isSubmitting || !isMutableForm}
            type="submit">
            {drugScreen ? 'Update' : 'Save'}
          </Button>
        </InputGroup>

        {showPreviewModal.showModal && showPreviewModal.values && (
          <DrugScreenPayoutPreview
            drugScreenId={drugScreen?.id}
            drugScreenPayload={showPreviewModal.values}
            onConfirm={handlePreviewConfirm}
          />
        )}
      </form>
    </Card>
  );
};

export default DrugScreenForm;
